import React, { useCallback, useState } from 'react';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  StyleSheet,
  View,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { Tabs, theme } from '@sg/garnish';

import {
  CustomizationContainer,
  CustomizationCTA,
  CustomizationDetailsContainer,
} from '@order/features/customization';

import { useDietaryRestrictions } from '../../../../../../components';
import { CUSTOMIZATIONS_TABS } from '../../../../constants';
import { useFilteredIngredientsModifications } from '../../../../hooks';
import { useProductDetailsScreenContext } from '../../../../ProductDetailsScreen.provider';
import { type IngredientModificationWithQuantity } from '../../../../types';
import { IngredientsGridPanel } from '../IngredientsGridPanel';
import { IngredientsReviewPanel } from '../IngredientsReviewPanel';
import { ProductDetailsContentNavigationHeader } from '../ProductDetailsNavigationHeader';
import {
  useCustomizationTelemetry,
  useProductDetailsIngredientsTabs,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsIngredientsViewXS = () => {
  const {
    addressId,
    restaurantSlug,
    product,
    modifications,
    quantity,
    activeIngredientsModificationsMap,
    addIngredientModification,
    removeIngredientModification,
    completelyRemoveIngredientModification,
    finishCustomization,
  } = useProductDetailsScreenContext();

  const ingredientModifications = product?.ingredientModifications;
  const { calories, netPriceChange } = modifications;

  const { tabs, activeTabItemId, setActiveTabItemId } =
    useProductDetailsIngredientsTabs();

  const getRestrictionNotice = useDietaryRestrictions({ referrer: 'ProductDetails' }); // prettier-ignore

  const isAddIngredientsActive =
    activeTabItemId === CUSTOMIZATIONS_TABS.ADD_INGREDIENTS;
  const isReviewActive = activeTabItemId === CUSTOMIZATIONS_TABS.REVIEW;

  // ─── Data ────────────────────────────────────────────────────────────

  const productCost = (product?.cost ?? 0) + netPriceChange;

  const { ingredientsModificationsKinds, ingredientsModifications } =
    useFilteredIngredientsModifications(ingredientModifications);

  const [defaultActiveTargetId, setDefaultActiveTargetId] = useState(
    ingredientsModificationsKinds[0],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const selectedIngredientsScrollSV = useSharedValue(0);

  const updateIngredientsScrollSV = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      // eslint-disable-next-line functional/immutable-data
      selectedIngredientsScrollSV.value = event.nativeEvent.contentOffset.y;
    },
    [selectedIngredientsScrollSV],
  );

  // ─── Telemetry ───────────────────────────────────────────────────────

  const {
    trackIngredientsTabPressed,
    trackReviewTabPressed,
    trackIngredientsTabIngredientAdded,
    trackIngredientsTabIngredientRemoved,
    trackReviewTabIngredientAdded,
    trackReviewTabIngredientRemoved,
    trackReviewTabAddFromSectionTapped,
  } = useCustomizationTelemetry();

  const changeTab = useCallback(
    (tab: string) => {
      setActiveTabItemId?.(tab);

      if (tab === CUSTOMIZATIONS_TABS.ADD_INGREDIENTS) {
        trackIngredientsTabPressed();

        return;
      }

      trackReviewTabPressed();
    },
    [setActiveTabItemId, trackIngredientsTabPressed, trackReviewTabPressed],
  );

  const goToSectionFromReview = useCallback(
    (section: (typeof ingredientsModificationsKinds)[number]) => {
      setDefaultActiveTargetId(section);
      trackReviewTabAddFromSectionTapped(section);
    },
    [trackReviewTabAddFromSectionTapped],
  );

  const addIngredientFromIngredientsTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      addIngredientModification(ingredientModification);
      trackIngredientsTabIngredientAdded();
    },
    [addIngredientModification, trackIngredientsTabIngredientAdded],
  );

  const removeIngredientFromIngredientsTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      removeIngredientModification(ingredientModification);
      trackIngredientsTabIngredientRemoved();
    },
    [removeIngredientModification, trackIngredientsTabIngredientRemoved],
  );

  const addIngredientFromReviewTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      trackReviewTabIngredientAdded();

      return addIngredientModification(ingredientModification);
    },
    [addIngredientModification, trackReviewTabIngredientAdded],
  );

  const removeIngredientFromReviewTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      trackReviewTabIngredientRemoved();

      return removeIngredientModification(ingredientModification);
    },
    [removeIngredientModification, trackReviewTabIngredientRemoved],
  );

  const removeIngredientCompletelyFromReviewTab = useCallback(
    (ingredientModification: IngredientModificationWithQuantity) => {
      completelyRemoveIngredientModification(ingredientModification);
      trackReviewTabIngredientRemoved();
    },
    [completelyRemoveIngredientModification, trackReviewTabIngredientRemoved],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!product) return null;

  return (
    <CustomizationContainer>
      <View style={styles.header}>
        <ProductDetailsContentNavigationHeader
          scrollOffsetSV={selectedIngredientsScrollSV}
          restaurantSlug={restaurantSlug}
          addressId={addressId}
          text={product.name}
          spacing="narrow"
        />

        <Tabs.ButtonBar
          tabs={tabs}
          activeTabId={activeTabItemId}
          onChange={changeTab}
        />
      </View>

      <CustomizationDetailsContainer
        scrollEventThrottle={16}
        onScroll={updateIngredientsScrollSV}
        bounces={false}
        isFluid={true}
      >
        <Tabs.Panel
          isActive={isAddIngredientsActive}
          tabId={CUSTOMIZATIONS_TABS.ADD_INGREDIENTS}
          panelId={CUSTOMIZATIONS_TABS.ADD_INGREDIENTS}
          style={isAddIngredientsActive ? styles.panel : null}
        >
          <IngredientsGridPanel
            modifications={modifications}
            ingredientsModificationsKinds={ingredientsModificationsKinds}
            ingredientsModifications={ingredientsModifications}
            activeIngredientsModificationsMap={activeIngredientsModificationsMap} // prettier-ignore
            defaultActiveTargetId={defaultActiveTargetId}
            addIngredientModification={addIngredientFromIngredientsTab}
            removeIngredientModification={removeIngredientFromIngredientsTab}
            getRestrictionNotice={getRestrictionNotice}
          />
        </Tabs.Panel>

        <Tabs.Panel
          isActive={isReviewActive}
          tabId={CUSTOMIZATIONS_TABS.REVIEW}
          panelId={CUSTOMIZATIONS_TABS.REVIEW}
          style={isReviewActive ? styles.panel : null}
        >
          <IngredientsReviewPanel
            product={product}
            modifications={modifications}
            addIngredientModification={addIngredientFromReviewTab}
            removeIngredientModification={removeIngredientFromReviewTab}
            completelyRemoveIngredientModification={removeIngredientCompletelyFromReviewTab} // prettier-ignore
            setActiveTabItemId={setActiveTabItemId}
            setDefaultActiveTargetId={goToSectionFromReview}
          />
        </Tabs.Panel>
      </CustomizationDetailsContainer>

      <CustomizationCTA
        variation="continue"
        cost={productCost}
        quantity={quantity}
        calories={calories}
        onPress={finishCustomization}
      />
    </CustomizationContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    backgroundColor: theme.colors.CREAM,
  },
  panel: {
    flex: 1,
  },
});
